<template lang="pug">
div.x-select
  v-text-field(
    v-if="isReadOnly || isDisabled || !isSelect"
    v-model="content"
    filled
    :loading="busy"
    :class="{ 'x-widget-readonly': isReadOnly }"
    :dense="!isLarge"
    :width="400"
    :label="myTitle"
    :hint="myHint"
    :hide-details="hasNoHints"
    :prepend-inner-icon="myIcon"
    :outlined="isOutlined"
    :disabled="isDisabled"
    readonly
  )

  v-combobox(
    v-else-if="allowsCustom"
    v-else
    v-model="content"
    v-bind="$attrs"
    ref="input"
    filled
    autocomplete
    :loading="busy"
    :clearable="!isRequired || allowsMany"
    :multiple="allowsMany"
    :items="filteredOptions"
    :class="classes"
    :label="myTitle"
    :hint="myHint"
    :error-messages="errorMessages"
    :dense="!isLarge"
    :autocomplete="myAutoComplete"
    :hide-details="hasNoHints"
    :rules="rules"
    :prepend-inner-icon="myIcon"
    :readonly="isReadOnly"
    :required="isRequired && !isDisabled"
    :disabled="isDisabled"
    density="compact"
    @keyup.enter="$emit('action-performed', $refs.input)"
  )
    template(#item="{ item, on, attrs }")
      v-list-item(
        v-bind="attrs"
        v-on="on"
        ripple
      )
        v-list-item-content
          v-list-item-title
            | {{ item.text }}
        v-list-item-action(v-if="useIcon")
          v-icon(small)
            | {{ item.icon }}

  v-select(
    v-else
    v-model="content"
    v-bind="$attrs"
    ref="input"
    filled
    autocomplete
    :loading="busy"
    :clearable="!isRequired || allowsMany"
    :multiple="allowsMany"
    :items="filteredOptions"
    :class="classes"
    :label="myTitle"
    :hint="myHint"
    :error-messages="errorMessages"
    :dense="!isLarge"
    :autocomplete="myAutoComplete"
    :hide-details="hasNoHints"
    :rules="rules"
    :prepend-inner-icon="myIcon"
    :readonly="isReadOnly"
    :required="isRequired && !isDisabled"
    :disabled="isDisabled"
    density="compact"
    @keyup.enter="$emit('action-performed', $refs.input)"
  )
    template(#item="{ item, on, attrs }")
      v-list-item(
        v-bind="attrs"
        v-on="on"
        ripple
      )
        v-list-item-content
          v-list-item-title
            | {{ item.text }}
        v-list-item-action(v-if="useIcon")
          v-icon(small)
            | {{ item.icon }}

    template(#prepend-item)
      v-text-field.x-select--search-field.m2(
        v-if="options.length > 9"
        v-model="searchTerm"
        single-line
        hide-details
        placeholder="Pesquisar..."
        clearable
        solo
        dense
        filled
        outlined
        height="30px"
        prepend-inner-icon="mdi-magnify"
        clear-icon="mdi-broom"
      )
</template>

<style lang="scss">
.x-select--search-field {
  * {
    min-height: initial !important;
  }
  .v-icon {
    zoom: 80% !important;
  }
}
</style>

<script>
import { mdiEyeOutline, mdiEyeOffOutline } from '@mdi/js'
import { interpolate, isInterpolated } from '@/helpers/strings'

export default {

  props: {
    /**
     * Objeto contendo os valores e campos do contexto no qual o widget está
     * inserido para inferência de propriedades aplicadas em interpolação de
     * templates de texto.
     */
    context: Object,

    /**
     * Instância opcional de um campo do Paper.
     * @type {Keep.Paper.Design.Field}
     */
    field: Object,

    /**
     * Um dos tipos de dados suportados pelo Paper.
     * @type {Keep.Paper.Design.TypeNames}
     */
    dataType: String,

    /**
     * Diz se o componente suporta seleção de intervalo.
     * @type {Boolean}
     */
    allowRange: Boolean,

    /**
     * Valor editado no componente.
     * @type {Any}
     */
    value: {
      validator: v => true
    },
  },

  data: () => ({
    searchTerm: '',

    busy: false,

    icons: {
      mdiEyeOutline,
      mdiEyeOffOutline,
    },

    dataSet: null,  // Um objeto do tipo {Keep.Paper.Design.DataSet}
    failure: null,  // Um objeto do tipo {Keep.Paper.Design.Status}
  }),

  computed: {
    myName: vm => vm.getIt('name'),
    myTitle: vm => vm.getIt('title') || vm.myName,
    myDescription: vm => vm.getIt('description'),
    myHint: vm => vm.getIt('hint'),
    myPlaceholder: vm => vm.getIt('placeholder'),
    isSecure: vm => vm.hasIt('secure'),
    isLarge: vm => vm.hasIt('large'),
    hasNoHints: vm => vm.hasIt('noHints'),
    isDisabled: vm => vm.hasIt('disabled'),
    isRequired: vm => vm.hasIt('required'),
    isReadOnly: vm => vm.hasIt('readOnly') || vm.hasIt('readonly'),
    isOutlined: vm => vm.hasIt('outlined'),
    myIcon: vm => vm.getIt('icon'),
    myFont: vm => vm.getIt('font'),

    allowsMany: vm => vm.hasIt('allowMany'),
    allowsCustom: vm => vm.hasIt('allowCustom'),

    myMultiline: vm => vm.getIt('multiline'),
    isSelect: vm => vm.hasIt('dataSet'),

    myAutoComplete () {
      if (this.myName?.toLowerCase() === 'username') return 'username'
      if (this.myName?.toLowerCase() === 'password') return 'current-password'
      return null
    },

    errorMessages () {
      return this.failure
        ? [ 'Não foi possível carregar os dados.', this.failure.message ]
        : null
    },

    options () {
      const sets = this.dataSet?.sets
      if (!sets?.length) return []

      // A propriedade "set" pode ser indicada para terminar qual conjunto de dados
      // será utilizado. Se não for indicada, o primeiro conjunto de dados será utilizado.
      const setName = this.getIt('set')
      const set = sets.filter(s => !setName || s.name === setName)[0]
      if (!set || !set.rows) return []

      const cols = set.headers || []
      const rows = set.rows

      // Por padrão, a primeira e a segunda coluna são usadas como chave e valor respectivamente.
      // Todavia, é possível personalizar estes campos pelo uso das propriedades:
      //    keyProperty, para determinar o campo chave.
      //    textProperty, para determinar o campo do texto exibido.
      // Note que, se não for possível determinar o campo valor, o campo chave será usado.
      const valueProperty = this.field.keyProperty
      const textProperty = this.field.textProperty
      const iconProperty = this.field.iconProperty

      const items = rows.map(row => {
        const properties = Object.keys(row).filter(property => !property.startsWith('@'))

        const localValueProperty = valueProperty || properties[0]
        const localTextProperty = textProperty || properties[1]
        const localIconProperty = iconProperty || 'icon'

        let value = localValueProperty ? getLiteralValue(row, localValueProperty) : undefined
        let text = localTextProperty ? getLiteralValue(row, localTextProperty) : undefined
        const icon = localIconProperty ? getLiteralValue(row, localIconProperty) : undefined

        // Condição especial. Se o set não contém chave ou não contém text é porque temos apenas
        // um valor definido para os dois campos. Vamos usar o primeiro não nulo.
        if (value === undefined || text === undefined) {
          value = text = value || text
        } else {

          // Determinando a visibilidade dos campos
          const valueCol = getCol(cols, localValueProperty)
          const textCol = getCol(cols, localTextProperty)

          const hideValue = !!valueCol?.hidden
          const hideText = !!textCol?.hidden

          if (hideText) {
            text = value
          } else if (!hideValue) {
            text = `${value} - ${text}`
          }
        }

        const item = { value, text, icon }

        if (item.value.value) item.value = item.value.value
        if (item.text.value) item.text = item.text.value
        return item
      })

      return items

      function getCol(cols, propertyName) {
        if (!cols) return

        propertyName = propertyName.toLowerCase()
        return cols.find(col => col?.name?.toLowerCase() === propertyName)
      }

      function getLiteralValue(row, propertyName) {
        const key = Object.keys(row).find(key => key.toLowerCase() === propertyName.toLowerCase())
        if (key === undefined) {
          return undefined
        }
        const cell = row[key]
        return cell?.value || cell
      }
    },

    filteredOptions() {
      const term = this.searchTerm ? this.searchTerm.toLowerCase() : ''
      if (!term) return this.options

      return this.options.filter(option =>
        option.text.toLowerCase().includes(term) ||
        option.value.toLowerCase().includes(term)
      )
    },

    useIcon () {
      return this.options.some(opt => !!opt.icon)
    },

    classes () {
      const classes = {}

      classes['x-widget-readonly'] = this.isReadOnly

      // FontStyle:
      //    Default = 0,
      //    Italic = 1,
      //    Bold = 2,
      //    Overline = 4,
      //    Strikethrough = 8,
      //    Underline = 16,
      //    Monospace = 32,
      const font = this.myFont || 0
      if ((font &  1) ==  1) classes['font-italic'] = true
      if ((font &  2) ==  2) classes['font-weight-bold'] = true
      if ((font &  4) ==  4) classes['text-decoration-overline'] = true
      if ((font &  8) ==  8) classes['text-decoration-line-through'] = true
      if ((font & 16) == 16) classes['text-decoration-underline'] = true
      if ((font & 32) == 32) classes['x-font-monospace'] = true

      return classes
    },

    metaIcon () {
      if (!this.isSecure) return null
      return this.revealed ? this.icons.mdiEyeOffOutline : this.icons.mdiEyeOutline
    },

    content: {
      get() {
        const items = this.parseItems(this.value)

        // Filtra e extrai valores em uma única passagem
        const validItems = items.reduce((acc, item) => {
          const value = item?.value ?? item
          if (value) {

            // A comparação não deve ser feita com '===' porque o valor pode ser de um tipo diferente
            const foundValue = this.options.find(option => option.value == value)

            if (foundValue) {
              acc.push(foundValue)
            } else {
              acc.push(value)
            }
          }
          return acc
        }, [])

        // Retorna um único item ou muitos, dependendo de 'allowsMany'
        return this.allowsMany ? validItems : validItems[0] || null
      },
      set(value) {
        const items = []

        if (value) {
          const array = Array.isArray(value) ? value : [value]
          array.forEach(item => {
            const value = item?.value ?? item
            if (value) items.push(value)
          })
        }

        let result = null
        if (this.allowsMany) {
          result = items.length ? items : null
        } else {
          result = items.length ? (items[0] || null) : null
        }

        this.$emit('input', result)
      }
    },

    rules () {
      const required = (this.isRequired && !this.isDisabled)
      return [
        v => !required || (!!v && (!Array.isArray(v) || !!v.length)) || 'Campo requerido',
        // v => (v && v.length <= 10) || 'Name must be less than 10 characters',
      ]
    },
  },

  watch: {
    $route () {
      this.loadOptionsAsync()
    },
  },

  mounted () {
    this.loadOptionsAsync()
  },

  methods: {
    getIt (property) {
      return this[property]
        || this.$props[property]
        || this.$attrs[property]
        || this.field?.[property]
    },
    hasIt (property) {
      if (this.$props[property] === '') return true
      if (this.$attrs[property] === '') return true
      const value = this[property]
        || this.$props[property]
        || this.$attrs[property]
        || this.field?.[property]
      return !!value
    },

    async loadOptionsAsync() {
      try {
        this.busy = true

        this.dataSet = null
        this.failure = null

        var dataSet = this.getIt('dataSet')
        if (!dataSet?.see) {
          this.dataSet = dataSet
          return
        }

        // Resolvendo a referência ao dataset...
        const result = await this.$resolve(dataSet.see, { noCache: true })
        if (!result.ok) {
          this.failure = result.entity
          return
        }

        this.dataSet = result.entity
      } finally {
        this.busy = false
      }
    },

    format(text) {
      return isInterpolated(text) ? interpolate(text, this.context) : text
    },

    /**
     * Garante sempre um array de itens.
     * - Se `value` for array, retorna ele mesmo.
     * - Se for string contendo ';', quebra pelos ';', dá trim e descarta strings vazias.
     * - Caso contrário, empacota o valor único num array.
     *
     * @param {*} value
     * @returns {Array<any>}
     */
    parseItems(value) {
      if (Array.isArray(value)) {
        return value;
      }
      if (typeof value === 'string' && value.includes(';')) {
        return value
          .split(';')
          .map(item => item.trim())
          .filter(item => item !== '');
      }
      return [value];
    },
  },
}
</script>